// Luxe · Project Stage Header — premium, understated projection of the
// canonical Brief -> Design -> Visualise -> Price -> Proposal -> Approval
// workflow onto the existing Section 01-09 workspace. Anchor-scrolls to
// the underlying section on click so the 2D editor / 3D viewer /
// pricing / proposal / portal flows remain intact.
import { useMemo } from "react";

const STAGES = [
  { key: "brief",     label: "Brief",     anchor: "stage-brief" },
  { key: "design",    label: "Design",    anchor: "stage-design" },
  { key: "visualise", label: "Visualise", anchor: "stage-visualise" },
  { key: "price",     label: "Price",     anchor: "stage-price" },
  { key: "proposal",  label: "Proposal",  anchor: "stage-proposal" },
  { key: "approval",  label: "Approval",  anchor: "stage-approval" },
];

function jumpTo(anchor) {
  const el = typeof document !== "undefined" && document.getElementById(anchor);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function computeProjectStageState(project, sceneVersions, proposals, activities) {
  const items = ((project || {}).schematic || {}).items || [];
  const hasBrief = !!(project?.client_id && project?.wall_width_mm && project?.wall_height_mm);
  const hasDesign = items.length > 0;
  const savedOptions = (sceneVersions || []).filter((s) => !s.archived_at);
  const hasSavedOption = savedOptions.length > 0;
  const props = proposals || [];
  const hasProposal = props.some((p) => (p.versions || []).length > 0);
  const anySent = props.some((p) => (p.versions || []).some((v) => v.status === "sent"));
  const accepted = (activities || []).some((a) => a.type === "proposal_accepted");
  // Derived flags for a fuller Next-Action matrix (Prompt 3C.1):
  //  • hasVisualised — at least one saved option has been opened in
  //    3D (approximated by the presence of a saved render package OR
  //    render_environment_pinned metadata on any saved Design).
  //  • hasPriced — at least one Saved Design has a linked estimate.
  //  • changesRequested — client submitted feedback since last send.
  const hasVisualised = savedOptions.some(
    (o) => (o.render_packages_count || 0) > 0 || !!o.render_environment_pinned,
  );
  const hasPriced = savedOptions.some((o) => !!o.latest_estimate_version_id);
  const changesRequested = (activities || []).some(
    (a) => a.type === "portal_feedback_submitted" || a.type === "client_changes_requested",
  );
  const done = {
    brief:     hasBrief,
    design:    hasDesign,
    visualise: hasVisualised,
    price:     hasPriced,
    proposal:  hasProposal,
    approval:  accepted,
  };
  let current = "brief";
  if (hasBrief)        current = "design";
  if (hasDesign)       current = "visualise";
  if (hasSavedOption)  current = "price";
  if (hasSavedOption)  current = "proposal";
  if (anySent)         current = "approval";
  if (accepted)        current = "approval";
  let nextAction = { key: "brief", label: "Complete Project Brief", anchor: "stage-brief" };
  if (!hasBrief)             nextAction = { key: "brief",     label: "Complete Project Brief",   anchor: "stage-brief"     };
  else if (!hasDesign)       nextAction = { key: "design",    label: "Start Designing",          anchor: "stage-design"    };
  else if (!hasSavedOption)  nextAction = { key: "design",    label: "Save This Design",         anchor: "stage-design"    };
  else if (!hasVisualised)   nextAction = { key: "visualise", label: "Review in 3D",             anchor: "stage-visualise" };
  else if (!hasPriced)       nextAction = { key: "price",     label: "Price This Design",        anchor: "stage-price"     };
  else if (!hasProposal)     nextAction = { key: "proposal",  label: "Create a Proposal",        anchor: "stage-proposal"  };
  else if (changesRequested) nextAction = { key: "approval",  label: "Review Client Feedback",   anchor: "stage-approval"  };
  else if (!anySent)         nextAction = { key: "proposal",  label: "Send Proposal to Client",  anchor: "stage-proposal"  };
  else if (!accepted)        nextAction = { key: "approval",  label: "Awaiting Client Response", anchor: "stage-approval"  };
  else                       nextAction = { key: "approval",  label: "Project Approved",         anchor: "stage-approval"  };
  return { done, current, nextAction };
}

export default function ProjectStageHeader({ project, sceneVersions, proposals, activities }) {
  const { done, current, nextAction } = useMemo(
    () => computeProjectStageState(project, sceneVersions, proposals, activities),
    [project, sceneVersions, proposals, activities],
  );
  return (
    <section data-testid="project-stage-header" className="border-y border-border py-5 -mx-6 lg:-mx-14 px-6 lg:px-14 mb-8 bg-neutral-50/60">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
        <div className="flex items-center flex-wrap gap-x-6 gap-y-2">
          {STAGES.map((s, idx) => {
            const isDone = done[s.key];
            const isCurrent = current === s.key;
            const glyph = isDone ? "\u2713" : isCurrent ? "\u25CF" : "\u25CB";
            return (
              <button key={s.key}
                      type="button"
                      data-testid={`project-stage-${s.key}`}
                      data-stage-done={isDone ? "true" : "false"}
                      data-stage-current={isCurrent ? "true" : "false"}
                      onClick={() => jumpTo(s.anchor)}
                      className="group flex items-center gap-2 text-xs">
                <span className="text-[10px] text-muted-foreground w-4 text-center">{String(idx + 1).padStart(2, "0")}</span>
                <span className={`w-3 text-center ${isDone ? "text-emerald-700" : isCurrent ? "text-foreground" : "text-muted-foreground"}`}>{glyph}</span>
                <span className={`tracking-widest uppercase ${isCurrent ? "text-foreground" : isDone ? "text-foreground/80" : "text-muted-foreground"} group-hover:text-foreground transition-colors`}>{s.label}</span>
              </button>
            );
          })}
        </div>
        <button type="button"
                data-testid="project-next-action"
                onClick={() => jumpTo(nextAction.anchor)}
                className="btn-primary text-xs shrink-0">
          {nextAction.label} <span aria-hidden>&rarr;</span>
        </button>
      </div>
    </section>
  );
}
